/**
 * Protege rutas autenticadas. Si el access token venció, intenta refresh
 * antes de redirigir a `/login`.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { PermisosService } from '../services/permisos.service';
import { DASHBOARD_ROUTE } from '../models/nav-item.model';

export const authGuard: CanActivateFn = async (_route, state) => {
  const authService = inject(AuthService);
  const permisosService = inject(PermisosService);
  const router = inject(Router);

  await authService.init();

  const oidcError = authService.getOidcInitError();
  if (oidcError) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url, loginError: 'oidc-unavailable' },
    });
  }

  if (!authService.isAuthenticated()) {
    const renewed = await authService.ensureValidAccessToken();
    if (!renewed) {
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }
  }

  if (!permisosService.isLoaded()) {
    try {
      await permisosService.loadPermisos();
    } catch (error) {
      authService.clearExpiredSession();
      let loginError = 'permisos';
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          loginError = 'session-expired';
        } else if (error.status === 403) {
          loginError = 'inactive';
        }
      }
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url, loginError },
      });
    }
  }

  const path = state.url.split('?')[0].replace(/\/$/, '') || '/';
  if (!permisosService.canAccessRoute(path)) {
    // Sin permiso para la ruta pedida: ir al home (siempre permitido si hay sesión).
    if (path === '/' || path === DASHBOARD_ROUTE) {
      return true;
    }
    return router.createUrlTree([DASHBOARD_ROUTE]);
  }

  return true;
};
